/**
 * "Edit binding" on a generated data-bound block: finding the structure under the block handle
 * and replacing it with a regenerated one in a single transaction (one undo).
 *
 * A line items table is two siblings — the header table and the loop — so either one finds both.
 * A conditional is edited in place instead of regenerated, which keeps whatever the user wrote
 * inside it; only its key, negation and else branch change.
 */
import type { Editor } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { NodeSelection } from '@tiptap/pm/state'
import { closeHistory } from '@tiptap/pm/history'
import { pmToBlocks } from '../cdm/toCdm'
import { normalizeMonikerKey } from '../cdm/monikerKey'
import { defaultParagraphFormat } from '../cdm/types'
import type { MovableBlock } from '../cdm/blockMove'
import { bindingJson, readBinding, type BindingSetup } from './bindingSetup'

export interface BindingTarget { from: number; to: number; current: BindingSetup }

const read = (...nodes: PMNode[]) => readBinding(pmToBlocks(nodes.map(n => n.toJSON())))

/** The generated structure the block at `block` belongs to, or null when it is not one. */
export function findBindingTarget(doc: PMNode, block: MovableBlock): BindingTarget | null {
  const { node, pos } = block
  const $pos = doc.resolve(pos)
  const parent = $pos.parent
  const index = $pos.index()
  const end = pos + node.nodeSize
  if (node.type.name === 'loop') {
    const prev = index > 0 ? parent.child(index - 1) : null
    if (prev?.type.name === 'table') {
      const current = read(prev, node)
      if (current) return { from: pos - prev.nodeSize, to: end, current }
    }
    const current = read(node)
    return current && { from: pos, to: end, current }
  }
  if (node.type.name === 'table') {
    const next = index + 1 < parent.childCount ? parent.child(index + 1) : null
    if (next?.type.name === 'loop') {
      const current = read(node, next)
      if (current) return { from: pos, to: end + next.nodeSize, current }
    }
    const current = read(node)
    return current?.kind === 'totals' ? { from: pos, to: end, current } : null
  }
  if (node.type.name === 'conditional') {
    const current = read(node)
    return current && { from: pos, to: end, current }
  }
  return null
}

/** True when changing `current` to `next` would drop an else branch that has content. */
export function dropsElseContent(doc: PMNode, target: BindingTarget, next: BindingSetup): boolean {
  if (target.current.kind !== 'conditional' || next.kind !== 'conditional' || next.withElse || !target.current.withElse) return false
  const elseBranch = doc.nodeAt(target.from)?.child(1)
  return !!elseBranch && (elseBranch.childCount > 1 || elseBranch.textContent !== '' || elseBranch.firstChild?.type.name !== 'paragraph')
}

export function applyBindingEdit(editor: Editor, target: BindingTarget, setup: BindingSetup): boolean {
  const { state, view } = editor
  const tr = closeHistory(state.tr)
  const node = state.doc.nodeAt(target.from)
  if (!node) return false
  try {
    if (setup.kind === 'conditional' && node.type.name === 'conditional') {
      const hasElse = node.childCount === 2
      if (setup.withElse && !hasElse) {
        const para = state.schema.nodes.paragraph.create({ format: defaultParagraphFormat() })
        tr.insert(target.from + node.nodeSize - 1, state.schema.nodes.branch.create({ kind: 'else' }, para))
      } else if (!setup.withElse && hasElse) {
        const bodyEnd = target.from + 1 + node.child(0).nodeSize
        tr.delete(bodyEnd, bodyEnd + node.child(1).nodeSize)
      }
      const rawName = setup.field.trim()
      tr.setNodeMarkup(target.from, undefined, { ...node.attrs, rawName, normalizedKey: normalizeMonikerKey(rawName), negate: setup.negate })
      tr.setSelection(NodeSelection.create(tr.doc, target.from))
    } else {
      const nodes = bindingJson(setup).map(json => state.schema.nodeFromJSON(json))
      tr.replaceWith(target.from, target.to, nodes)
      tr.setSelection(NodeSelection.create(tr.doc, target.from))
    }
  } catch {
    return false
  }
  view.dispatch(tr.scrollIntoView())
  view.focus()
  return true
}
